import type { FC, FocusEvent, ReactNode, SelectHTMLAttributes } from 'react'
import classNames from 'classnames'
import { type FieldValues, type RegisterOptions, useFormContext } from 'react-hook-form'
import { Label } from '../../shared/compass-ui'

export interface ControlledSelectOption {
  label: string
  value: string
}

export interface ControlledSelectProps extends Omit<
  SelectHTMLAttributes<HTMLSelectElement>,
  'name'
> {
  id: string
  name: string
  label: ReactNode
  options: readonly ControlledSelectOption[]
  error?: boolean
  errorId?: string
  placeholderValue?: string
  registerOptions?: RegisterOptions<FieldValues, string>
}

const ControlledSelect: FC<ControlledSelectProps> = ({
  id,
  name,
  label,
  options,
  error = false,
  errorId,
  placeholderValue = '',
  registerOptions,
  className,
  onBlur,
  ...selectProps
}) => {
  const { register } = useFormContext()
  const registered = register(name, registerOptions)

  const handleBlur = (event: FocusEvent<HTMLSelectElement>) => {
    registered.onBlur(event)
    onBlur?.(event)
  }

  return (
    <>
      <Label htmlFor={id}>{label}</Label>
      <select
        defaultValue={placeholderValue}
        {...selectProps}
        {...registered}
        id={id}
        aria-describedby={error && errorId ? errorId : undefined}
        onBlur={handleBlur}
        className={classNames(
          'w-full h-[60px] border-2 font-normal p-m pr-xl rounded bg-white bg-chevron bg-no-repeat bg-[right_16px_center]',
          {
            'border-red-600 outline-red-600': error,
            'border-ink-400 focus:border-blue-600 outline-blue-600': !error,
          },
          className
        )}
      >
        <option disabled value={placeholderValue}></option>
        {options.map(({ label: optionLabel, value }) => (
          <option key={value} value={value}>
            {optionLabel}
          </option>
        ))}
      </select>
    </>
  )
}

ControlledSelect.displayName = 'ControlledSelect'

export default ControlledSelect
